import DOMPurify from 'dompurify';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { SHOW } from 'src/components/DisplayWrapper';
import { sendSignalsAction } from 'src/redux/componentSignals/actions';
import { COMMENTS_TARGET_ID } from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import { changeViewStateAction } from 'src/redux/viewState/actions';
import { showNotification } from 'src/utils';

interface Props {
  performAnchorAddingRequest: (...args: any[]) => void;
  sectionId?: string | number;
  text?: string;
  isExtra?: boolean;
  narrativeId?: string | number | null;
}

interface SavedSelection {
  start: number;
  end: number;
}

const ANCHOR_BACKGROUND = '#e3825b';
const ANCHOR_COLOR = '#F9B000';

function exportSelection(root: HTMLElement): SavedSelection | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) {
    return null;
  }

  const preRange = range.cloneRange();
  preRange.selectNodeContents(root);
  preRange.setEnd(range.startContainer, range.startOffset);
  const start = preRange.toString().length;
  const end = start + range.toString().length;

  if (end <= start) return null;

  return { start, end };
}

function importSelection(
  root: HTMLElement,
  { start, end }: SavedSelection,
): Range | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  let charIndex = 0;
  let startNode: Node | null = null;
  let startOffset = 0;
  let endNode: Node | null = null;
  let endOffset = 0;

  let node = walker.nextNode();
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (startNode === null && start <= charIndex + len) {
      startNode = node;
      startOffset = start - charIndex;
    }
    if (endNode === null && end <= charIndex + len) {
      endNode = node;
      endOffset = end - charIndex;
      break;
    }
    charIndex += len;
    node = walker.nextNode();
  }

  if (!startNode || !endNode) return null;

  const range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);
  return range;
}

function wrapRangeWithAnchor(
  range: Range,
  createAnchor: () => HTMLAnchorElement,
): void {
  const ancestor =
    range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentNode
      : range.commonAncestorContainer;

  if (!ancestor) return;

  const walker = document.createTreeWalker(ancestor, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let node = walker.nextNode();
  while (node) {
    if (range.intersectsNode(node)) {
      textNodes.push(node as Text);
    }
    node = walker.nextNode();
  }

  textNodes.forEach(textNode => {
    let target: Text = textNode;

    if (target === range.startContainer && target === range.endContainer) {
      if (range.endOffset < target.length) target.splitText(range.endOffset);
      if (range.startOffset > 0) target = target.splitText(range.startOffset);
    } else if (target === range.startContainer) {
      if (range.startOffset > 0) target = target.splitText(range.startOffset);
    } else if (target === range.endContainer) {
      if (range.endOffset < target.length) target.splitText(range.endOffset);
    }

    if (!target.textContent) return;

    const anchor = createAnchor();
    target.parentNode?.insertBefore(anchor, target);
    anchor.appendChild(target);
  });
}

function selectionContainsLink(range: Range): boolean {
  const boundaryElement = (container: Node): Element | null =>
    container.nodeType === Node.ELEMENT_NODE
      ? (container as Element)
      : container.parentElement;

  if (
    boundaryElement(range.startContainer)?.closest('a') ||
    boundaryElement(range.endContainer)?.closest('a')
  ) {
    return true;
  }

  const fragment = range.cloneContents();
  return fragment.querySelector('a') !== null;
}

export const AnchorModal = ({
  performAnchorAddingRequest,
  sectionId,
  text,
  isExtra,
  narrativeId = null,
}: Props) => {
  const dispatch = useDispatch();

  const contentRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<SavedSelection | null>(null);

  const [toolbar, setToolbar] = useState<{
    top: number;
    left: number;
    placement: 'top' | 'bottom';
    mode: 'button' | 'form';
    alreadyHighlighted: boolean;
  } | null>(null);
  const [title, setTitle] = useState('');

  const completedDraftComment = useSelector((state: RootState) =>
    state.viewState?.draftComment?.isCompleted
      ? state.viewState?.draftComment
      : undefined,
  );

  const sanitizedText = useMemo(() => DOMPurify.sanitize(text || ''), [text]);

  const hideToolbar = useCallback(() => {
    setToolbar(null);
    setTitle('');
    savedSelectionRef.current = null;
  }, []);

  const handleMouseUp = useCallback(() => {
    window.setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        hideToolbar();
        return;
      }

      const range = selection.getRangeAt(0);
      if (!root.contains(range.commonAncestorContainer)) {
        hideToolbar();
        return;
      }

      const saved = exportSelection(root);
      if (!saved) {
        hideToolbar();
        return;
      }
      savedSelectionRef.current = saved;

      const rangeRect = range.getBoundingClientRect();

      const TOOLBAR_CLEARANCE = 70;
      const placement: 'top' | 'bottom' =
        rangeRect.top < TOOLBAR_CLEARANCE ? 'bottom' : 'top';

      const anchorTop = placement === 'top' ? rangeRect.top : rangeRect.bottom;

      setTitle('');
      setToolbar({
        top: anchorTop + window.scrollY,
        left: rangeRect.left + rangeRect.width / 2 + window.scrollX,
        placement,
        mode: 'button',
        alreadyHighlighted: selectionContainsLink(range),
      });
    }, 0);
  }, [hideToolbar]);

  const handleContentClick = useCallback(
    (event: React.MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a');
      if (anchor && anchor.id) {
        dispatch(changeViewStateAction({ highlightComment: anchor.id }));
        dispatch(sendSignalsAction({ [COMMENTS_TARGET_ID]: { [SHOW]: true } }));
        event.preventDefault();
      }
    },
    [dispatch],
  );

  const openForm = useCallback(() => {
    setToolbar(current => (current ? { ...current, mode: 'form' } : current));
  }, []);

  const saveComment = useCallback(() => {
    const name = title.trim();
    const saved = savedSelectionRef.current;
    if (!name || !saved) return;

    const anchorID = `${narrativeId ?? sectionId}-highlight-${uuidv4()}`;

    const draftComment: any = {
      title: name.replaceAll('"', "''"),
      content: '',
      anchorID,
      selection: saved,
      isCompleted: false,
      isExtra,
    };

    if (narrativeId) {
      draftComment.narrativeId = narrativeId;
    }

    dispatch(changeViewStateAction({ draftComment }));
    dispatch(sendSignalsAction({ [COMMENTS_TARGET_ID]: { [SHOW]: true } }));

    window.getSelection()?.removeAllRanges();
    hideToolbar();
  }, [title, narrativeId, sectionId, isExtra, dispatch, hideToolbar]);

  useEffect(() => {
    if (!toolbar) return undefined;

    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        toolbarRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      ) {
        return;
      }
      hideToolbar();
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [toolbar, hideToolbar]);

  useEffect(() => {
    if (
      !completedDraftComment ||
      isExtra !== completedDraftComment.isExtra ||
      (narrativeId !== null &&
        narrativeId !== completedDraftComment.narrativeId)
    ) {
      return;
    }

    const root = contentRef.current;
    if (!root) return;

    const {
      selection,
      title: anchorName,
      anchorID,
      content,
    } = completedDraftComment;

    try {
      const range = importSelection(root, selection);
      if (!range) {
        throw new Error('Unable to restore the selection');
      }

      wrapRangeWithAnchor(range, () => {
        const anchor = document.createElement('a');
        anchor.className = 'comment-anchor';
        anchor.id = anchorID;
        anchor.setAttribute('name', anchorName);
        anchor.style.backgroundColor = ANCHOR_BACKGROUND;
        anchor.style.color = ANCHOR_COLOR;
        return anchor;
      });

      window.getSelection()?.removeAllRanges();

      if (narrativeId) {
        performAnchorAddingRequest(
          root.innerHTML,
          anchorName,
          anchorID,
          content,
          narrativeId,
        );
      } else {
        performAnchorAddingRequest(
          root.innerHTML,
          anchorName,
          anchorID,
          content,
        );
      }
    } catch (e) {
      console.log('error while creating the comment anchor', e);
      showNotification(
        'Error while creating the comment. Please retry',
        'error',
      );
      dispatch(changeViewStateAction({ draftComment: undefined }));
    }
  }, [completedDraftComment, narrativeId]);

  return (
    <div className="comment-anchor-wrapper">
      <div
        className="medium-editor-element"
        ref={contentRef}
        onMouseUp={handleMouseUp}
        onClick={handleContentClick}
        dangerouslySetInnerHTML={{ __html: sanitizedText }}
      />

      {toolbar &&
        createPortal(
          <div
            ref={toolbarRef}
            role="toolbar"
            aria-label="Comment tools"
            className={`medium-editor-toolbar medium-editor-toolbar-active ${
              toolbar.placement === 'top'
                ? 'medium-toolbar-arrow-under'
                : 'medium-toolbar-arrow-over'
            }${
              toolbar.mode === 'button' && toolbar.alreadyHighlighted
                ? ' medium-editor-toolbar-message'
                : ''
            }`}
            style={{
              top: toolbar.top,
              left: toolbar.left,
              transform:
                toolbar.placement === 'top'
                  ? 'translate(-50%, calc(-100% - 8px))'
                  : 'translate(-50%, 8px)',
            }}
            onMouseDown={event => event.preventDefault()}
          >
            {toolbar.mode === 'button' ? (
              <ul className="medium-editor-toolbar-actions">
                <li>
                  {toolbar.alreadyHighlighted ? (
                    <button
                      type="button"
                      className="medium-editor-action"
                      disabled
                    >
                      This text has already been highlighted
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="medium-editor-action"
                      onClick={openForm}
                    >
                      <b>Create Comment</b>
                    </button>
                  )}
                </li>
              </ul>
            ) : (
              <div className="medium-editor-toolbar-form medium-editor-toolbar-form-active">
                <input
                  type="text"
                  className="medium-editor-toolbar-input"
                  placeholder="Title here..."
                  aria-label="Comment title"
                  value={title}
                  autoFocus
                  onMouseDown={event => event.stopPropagation()}
                  onChange={event => setTitle(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      saveComment();
                    } else if (event.key === 'Escape') {
                      hideToolbar();
                    }
                  }}
                />
                <button
                  type="button"
                  className="medium-editor-toolbar-close"
                  aria-label="Cancel"
                  onClick={hideToolbar}
                >
                  &times;
                </button>
                <button
                  type="button"
                  className="medium-editor-toolbar-save"
                  aria-label="Save comment title"
                  onClick={saveComment}
                >
                  &#10003;
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default AnchorModal;
